/**
 * @ Author: Housefolios
 * @ Create Time: 2025-01-27 00:00:00
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-23 14:30:35
 * @ Description: Generic delete confirmation dialog component
 */

import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  Box,
} from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import {
  deleteDialogIconBlobSx,
  deleteDialogConfirmTextSx,
  deleteDialogDangerBtnSx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'

/**
 * Generic Delete Confirmation Dialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog should close
 * @param {Function} props.onConfirm - Function to call when delete is confirmed
 * @param {string} props.title - Title for the dialog (e.g., "Delete Portfolio?")
 * @param {string} props.confirmationText - Text for the confirmation checkbox
 * @param {string} props.successMessage - Success message to show after deletion
 * @param {string} props.errorMessage - Error message to show if deletion fails
 * @param {boolean} props.loading - Whether the deletion is in progress
 * @param {string} props.loadingMessage - Message to show during loading
 * @param {boolean} props.showSharedWarning - Whether to show shared portfolio warning
 * @param {string} props.sharedWarningText - Custom text for shared warning
 * @param {React.ReactNode} props.children - Additional content to render in dialog
 * @param {string} props.icon - FontAwesome icon name (default: 'trash-can' })
 * @param {string} props.iconStyle - FontAwesome icon style (default: 'far' })
 * @param {string} props.cancelButtonText - Text for cancel button (default: 'Cancel' })
 * @param {string} props.deleteButtonText - Text for delete button (default: 'Delete' })
 * @param {boolean} props.requireConfirmation - Whether checkbox confirmation is required (default: true })
 */
function DeleteConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title = 'Delete Item?',
  confirmationText = 'This action cannot be undone.',
  successMessage = 'Item deleted successfully',
  errorMessage = 'Failed to delete item',
  loading = false,
  loadingMessage = 'Deleting...',
  showSharedWarning = false,
  sharedWarningText = 'This item is shared with others',
  children = null,
  icon = 'trash-can',
  iconStyle = 'far',
  cancelButtonText = 'Cancel',
  deleteButtonText = 'Delete',
  requireConfirmation = true,
}) {
  const [confirm, setConfirm] = useState(false)

  const handleClose = () => {
    setConfirm(false)
    onClose()
  }

  const handleConfirm = async () => {
    if (requireConfirmation && !confirm) return

    try {
      await onConfirm()
      enqueueSnackbar(successMessage, {
        variant: 'success',
        autoHideDuration: 3000,
      })
      handleClose()
    } catch (error) {
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 3000,
      })
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.stopPropagation()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
    >
      <BlockUi
        blocking={loading}
        loader={
          <PacmanLoader color={'var(--primary)'} loading={true} />
        }
        message={
          <Box sx={{ color: '#368cff' }}>{loadingMessage}</Box>
        }
      >
        <Box sx={{ textAlign: 'center', p: '3rem' }}>
          <Box sx={[{ borderRadius: '50%', m: 0 }, {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mr: "0.25rem",
            position: "relative",
            fontSize: "1.2rem",
            transition: "all 0.2s ease-in-out",
            "&:last-child": { mr: 0, marginRight: 0 },
            "& .badge": { position: "absolute", right: 0, bottom: 0 },
            "& svg, & .svg-inline--fa": { display: "block" },
            marginRight: "0.25rem",
          }]}>
            <Box data-hf="delete-icon-blob" sx={deleteDialogIconBlobSx}>
              <FontAwesomeIcon icon={[iconStyle, icon]} size="10x" style={{ display: 'flex', alignSelf: 'center' }} sx={{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }} />
            </Box>
          </Box>

          <Box component="h4" sx={{ fontWeight: 700, mt: '1.5rem' }}>
            {title}
          </Box>

          {showSharedWarning && (
            <>
              <h5>{sharedWarningText}</h5>
            </>
          )}

          {children}

          {requireConfirmation && (
            <Box sx={deleteDialogConfirmTextSx}>
              <FormControl component="fieldset" sx={{ pr: '1.5rem' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirm}
                        onChange={() => setConfirm(!confirm)}
                      />
                    }
                    label={confirmationText}
                  />
                </FormGroup>
              </FormControl>
            </Box>
          )}

          <Box sx={{ pt: '1.5rem' }}>
            <Button variant="text" onClick={handleClose}>
              <Box component="span" sx={{
                display: "inline-block",
                "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
              }}>
                {cancelButtonText}
              </Box>
            </Button>
            <Button
              variant="contained"
              data-hf="delete-danger-btn"
              sx={deleteDialogDangerBtnSx}
              onClick={handleConfirm}
              disabled={requireConfirmation ? !confirm : false}
            >
              <Box component="span" sx={{
                display: "inline-block",
                "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
              }}>
                {deleteButtonText}
              </Box>
            </Button>
          </Box>
        </Box>
      </BlockUi>
    </Dialog>
  )
}

export default DeleteConfirmationDialog
